import { css, cx } from "@emotion/css";
import { Button, Switch } from "@octopusdeploy/design-system-components";
import { CheckCircleIcon, ExclamationTriangleIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, borderWidth, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ArgoCDApplicationAnnotation, ArgoCDApplicationResource, ArgoCDApplicationSourceResource } from "@octopusdeploy/octopus-server-client";
import { isHelmApplicationSource, ArgoCDAnnotationKeys } from "@octopusdeploy/octopus-server-client";
import { sortBy } from "lodash";
import React, { useMemo, useState } from "react";
import { GitCredentialDrawer } from "~/areas/library/components/GitCredentials/GitCredentialDrawer";
import { Code } from "~/components/Code/Code";
const styles = {
    container: css({
        backgroundColor: themeTokens.color.background.secondary.default,
        display: "flex",
        flexDirection: "column",
        gap: space[32],
        padding: space[16],
    }),
    sectionContainer: css({
        display: "flex",
        flexDirection: "column",
        gap: space[16],
    }),
    heading: css({
        font: text.heading.small,
        display: "flex",
        flexDirection: "row",
        gap: space[8],
        alignItems: "center",
    }),
    itemContainer: css({
        padding: space[16],
        borderRadius: borderRadius["large"],
        backgroundColor: themeTokens.color.background.primary.default,
        boxShadow: themeTokens.shadow.extraSmall,
        display: "flex",
        flexDirection: "column",
        gap: space[16],
    }),
    repositoryUrlWithIcon: css({
        font: text.body.regular.medium,
        color: themeTokens.color.text.primary,
        display: "flex",
        flexDirection: "row",
        gap: space[4],
        alignItems: "center",
        margin: 0,
    }),
    codeContainer: css({
        border: `${borderWidth[1]} solid ${themeTokens.color.border.primary}`,
        borderRadius: borderRadius["small"],
        //this is required for the outer border radius to properly clip content
        overflow: "auto",
    }),
    splitFlex: css({
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
    }),
    itemWarningHeader: css({
        font: text.body.bold.medium,
        color: themeTokens.color.text.primary,
        display: "flex",
        flexDirection: "row",
        gap: space[4],
        alignItems: "center",
        margin: 0,
        marginBottom: space[8],
    }),
    itemWarningSubtitle: css({
        font: text.body.regular.medium,
        color: themeTokens.color.text.secondary,
    }),
    itemWarningList: css({
        listStyleType: "disc",
        margin: 0,
        marginLeft: space[16],
    }),
    itemWarningListItem: css({
        color: themeTokens.color.text.secondary,
        font: text.body.regular.medium,
        marginBottom: space[4],
        "&:last-of-type": {
            marginBottom: 0,
        },
    }),
    inlineFlex: css({
        display: "inline-flex",
        width: "100%",
    }),
};
function getSourcesWithCredentials(application: ArgoCDApplicationResource) {
    return application.Sources.filter((src) => !isHelmApplicationSource(src));
}
export function hasSourcesWithoutMatchedGitCredentials(application: ArgoCDApplicationResource) {
    //we don't require git credentials
    return getSourcesWithCredentials(application).some((src) => !src.BestMatchedGitCredentialId);
}
export function isMissingRequiredAnnotations(application: ArgoCDApplicationResource, requiresEnvironmentAnnotation: boolean = true) {
    return !application.Annotations[ArgoCDAnnotationKeys.Project] || (requiresEnvironmentAnnotation && !application.Annotations[ArgoCDAnnotationKeys.Environment]) || isMissingRequiredHelmAnnotations(application);
}
function hasAnyHelmSources(application: ArgoCDApplicationResource) {
    return application.Sources.some((src) => isHelmApplicationSource(src));
}
function isMissingRequiredHelmAnnotations(application: ArgoCDApplicationResource) {
    const anyHelmSources = hasAnyHelmSources(application);
    const hasAnyHelmAnnotations = Object.keys(application.Annotations).some((key) => key.startsWith(ArgoCDAnnotationKeys.HelmImageReplacePathPrefix));
    return anyHelmSources && !hasAnyHelmAnnotations;
}
type ArgoCDApplicationDetailsProps = {
    application: ArgoCDApplicationResource;
    onGitCredentialAdded: () => void;
    onGenerateAnnotationsClick: () => void;
    onGenerateHelmAnnotationsClick: () => void;
};
const annotationSortOrders: Record<ArgoCDApplicationAnnotation, number> = {
    [ArgoCDAnnotationKeys.Project]: 1,
    [ArgoCDAnnotationKeys.Environment]: 2,
    [ArgoCDAnnotationKeys.Tenant]: 3,
    [ArgoCDAnnotationKeys.HelmImageReplacePathPrefix]: 4,
    [ArgoCDAnnotationKeys.DefaultImageRegistry]: 5,
};
// We set any unknown annotations sort order to be much higher to force them at the end of the list
const unknownAnnotationSortOrder = 100;
export const ArgoCDApplicationDetails = (props: ArgoCDApplicationDetailsProps) => {
    const { application, onGitCredentialAdded } = props;
    const [showAllAnnotations, setShowAllAnnotations] = useState<boolean>(false);
    const [appSourceToAddCredentialFor, setAppSourceToAddCredentialFor] = useState<ArgoCDApplicationSourceResource | undefined>(undefined);
    const [annotationsYaml, missingRequiredAnnotations] = useMemo(() => {
        const allRequiredAnnotations: ArgoCDApplicationAnnotation[] = [ArgoCDAnnotationKeys.Project, ArgoCDAnnotationKeys.Environment];
        let annotationYaml = `annotations:`;
        const sortedAnnotations = sortBy(Object.entries(application.Annotations), ([key]) => {
            //the key might be a prefix (image replace paths), so we look through the sort orders and compare using prefixes
            const [, sortOrder] = Object.entries(annotationSortOrders).find(([k]) => key.startsWith(k)) || ["", unknownAnnotationSortOrder];
            return sortOrder;
        });
        const filteredAnnotations = showAllAnnotations ? sortedAnnotations : sortedAnnotations.filter(([key]) => key.startsWith("argo.octopus.com"));
        for (const [key, value] of filteredAnnotations) {
            if (value) {
                annotationYaml += `\n  ${key}: "${value}"`;
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                const rqdIdx = allRequiredAnnotations.indexOf(key as ArgoCDApplicationAnnotation);
                if (rqdIdx >= 0) {
                    allRequiredAnnotations.splice(rqdIdx, 1);
                }
            }
        }
        return [annotationYaml, allRequiredAnnotations];
    }, [application.Annotations, showAllAnnotations]);
    const sourcesWithCredentials = getSourcesWithCredentials(application);
    const sourcesWithoutMatchedGitCredentials = hasSourcesWithoutMatchedGitCredentials(application);
    const hasMissingAnnotations = missingRequiredAnnotations.length > 0;
    const missingRequiredHelmAnnotations = useMemo(() => isMissingRequiredHelmAnnotations(application), [application]);
    return (<>
            <div className={styles.container}>
                {sourcesWithCredentials.length > 0 && (<div className={styles.sectionContainer}>
                        <div className={styles.heading}>Source repositories</div>
                        {sourcesWithoutMatchedGitCredentials && (<div className={styles.itemContainer}>
                                <div>
                                    <div className={styles.itemWarningHeader}>
                                        <ExclamationTriangleIcon size={20} color={themeTokens.color.icon.warning}/>
                                        Missing Git Credentials
                                    </div>
                                    <div className={styles.itemWarningSubtitle}>Octopus needs write access to each source repository</div>
                                </div>
                            </div>)}
                        {sourcesWithCredentials.map((source) => (<div key={source.RepositoryUrl} className={cx(styles.itemContainer, styles.splitFlex)}>
                                <div className={styles.repositoryUrlWithIcon}>
                                    {source.BestMatchedGitCredentialId ? <CheckCircleIcon size={20} color={themeTokens.color.icon.success}/> : <ExclamationTriangleIcon size={20} color={themeTokens.color.icon.tertiary}/>}
                                    <span>{source.RepositoryUrl}</span>
                                </div>
                                {!source.BestMatchedGitCredentialId && (<div>
                                        <Button importance={"quiet"} label={"Add Git Credential"} size={"medium"} onClick={() => setAppSourceToAddCredentialFor(source)}/>
                                    </div>)}
                            </div>))}
                    </div>)}
                <div className={styles.sectionContainer}>
                    <div className={styles.heading}>
                        <span> Application annotations</span>
                    </div>
                    <div className={styles.itemContainer}>
                        {(hasMissingAnnotations || missingRequiredHelmAnnotations) && (<div>
                                <div className={styles.itemWarningHeader}>
                                    <ExclamationTriangleIcon size={20} color={themeTokens.color.icon.warning}/>
                                    Missing annotations
                                </div>
                                <ul className={styles.itemWarningList}>
                                    {hasMissingAnnotations && (<li className={styles.itemWarningListItem}>
                                            <div className={cx(styles.splitFlex, styles.inlineFlex)}>
                                                <span>Missing project and environment annotations</span>
                                                <Button importance={"quiet"} label={"Generate Scoping Annotations"} onClick={() => props.onGenerateAnnotationsClick()}/>
                                            </div>
                                        </li>)}
                                    {missingRequiredHelmAnnotations && (<li className={styles.itemWarningListItem}>
                                            <div className={cx(styles.splitFlex, styles.inlineFlex)}>
                                                <span>Helm requires additional annotations</span>
                                                <Button importance={"quiet"} label={"Generate Helm Annotations"} onClick={() => props.onGenerateHelmAnnotationsClick()}/>
                                            </div>
                                        </li>)}
                                </ul>
                            </div>)}
                        <div className={styles.codeContainer}>
                            <Code value={annotationsYaml} language={"YAML"} showLineNumbers={false} showCodeFold={false} lineWrapping={true}/>
                        </div>
                        <div>
                            <Switch value={showAllAnnotations} onChange={setShowAllAnnotations} label={"Show all annotations"} size={"small"}/>
                        </div>
                    </div>
                </div>
            </div>
            <GitCredentialDrawer isOpen={!!appSourceToAddCredentialFor} onClose={() => setAppSourceToAddCredentialFor(undefined)} onSave={() => {
            onGitCredentialAdded();
            setAppSourceToAddCredentialFor(undefined);
        }} initialAllowedRepositoryUrls={appSourceToAddCredentialFor ? [appSourceToAddCredentialFor.RepositoryUrl] : undefined}/>
        </>);
};
